import { Button } from '@/components/ui/button'
import { TagItem } from '@/hooks/api/use-table-details-query'
import { cn } from '@/lib/utils'
import React from 'react'
import { FIELD_DISPLAY_NAMES, TagCategory } from '../constants'
import { EditableCellProps, EditingState } from '../types'
import { DescriptionCell, TagsCell } from './tables-common-cells'

export const EditableCell = React.memo<EditableCellProps>(
  ({
    fieldName,
    value,
    columnName,
    isEditing,
    isEditingDisabled,
    onEdit,
    onCellClick,
    onBlur,
    textareaRefs,
    editingState,
  }) => {
    const hasBeenEdited =
      fieldName === 'table_description'
        ? editingState.tables[columnName] !== undefined
        : editingState.columns[columnName] !== undefined

    const displayValue = (() => {
      if (fieldName === 'table_description') {
        return editingState.tables[columnName]?.table_description ?? value
      } else {
        return hasBeenEdited ? (editingState.columns[columnName][fieldName] ?? value) : value
      }
    })()

    if (isEditing) {
      return (
        <textarea
          ref={(el) => {
            if (el) {
              textareaRefs.current[`${columnName}_${fieldName}`] = el
            }
          }}
          className='h-full min-h-[72px] w-full rounded-md border-2 border-mango-200 bg-white p-2 text-sm transition-colors focus:border-mango-300 focus:outline-none'
          value={displayValue}
          onChange={(e) => onEdit(columnName, e.target.value)}
          onBlur={onBlur}
          onFocus={(e) => {
            // Ensure cursor is at end of text when textarea receives focus
            const textarea = e.target
            const endPosition = textarea.value.length
            if (endPosition > 0) {
              textarea.setSelectionRange(endPosition, endPosition)
            }
          }}
          rows={4}
          maxLength={500}
          autoFocus
          style={{
            fontFamily: 'inherit',
            lineHeight: 'inherit',
          }}
        />
      )
    }

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        if (!isEditingDisabled) {
          onCellClick(columnName, fieldName)
        }
      }
    }

    const getAriaLabel = () => {
      if (isEditingDisabled) {
        return undefined
      }
      return hasBeenEdited
        ? `Edit ${FIELD_DISPLAY_NAMES[fieldName]} (Modified)`
        : `Edit ${FIELD_DISPLAY_NAMES[fieldName]}`
    }

    const getTitle = () => {
      if (isEditingDisabled) {
        return undefined
      }
      return hasBeenEdited
        ? `Click to edit ${FIELD_DISPLAY_NAMES[fieldName]} (Modified)`
        : `Click to edit ${FIELD_DISPLAY_NAMES[fieldName]}`
    }

    return (
      <Button
        unstyled
        type='button'
        className={cn(
          'w-full rounded-md border-2 border-transparent p-2 text-left transition-all duration-200',
          !isEditingDisabled
            ? 'cursor-pointer hover:border-mango-200 hover:bg-white/50 focus:border-mango-300 focus:outline-none'
            : 'cursor-auto',
        )}
        onClick={isEditingDisabled ? undefined : () => onCellClick(columnName, fieldName)}
        onKeyDown={isEditingDisabled ? undefined : handleKeyDown}
        disabled={isEditingDisabled}
        aria-label={getAriaLabel()}
        title={getTitle()}
      >
        <div className='relative'>
          <DescriptionCell data={displayValue} />
          {hasBeenEdited && (
            <div className='bg-mango-400 absolute -right-1 -top-1 h-2 w-2 rounded-full'></div>
          )}
        </div>
      </Button>
    )
  },
)

EditableCell.displayName = 'EditableCell'

interface EditableTagsProps {
  columnName: string
  tags: TagItem[]
  category: TagCategory
  editingState: EditingState
  onTagDelete: (columnName: string, tagToDelete: string) => void
  isEditingDisabled?: boolean
}

export const EditableTags = React.memo<EditableTagsProps>(
  ({ columnName, tags, category, editingState, onTagDelete, isEditingDisabled = false }) => {
    const handleTagDelete = (tagToDelete: string) => {
      onTagDelete(columnName, tagToDelete)
    }

    return (
      <TagsCell
        tags={tags}
        category={category}
        onTagDelete={handleTagDelete}
        isEditingDisabled={isEditingDisabled}
      />
    )
  },
)

EditableTags.displayName = 'EditableTags'
